import type { FormSignerMapping } from './formSignerMapping';

// Rows link by normalized email only. Form Service uses the same rule to merge
// rows into one recipient. Keep both in sync.
export const normalizeText = (value: string | null | undefined) =>
  (value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

const getIdentity = (row: FormSignerMapping) =>
  normalizeText(row.email) || undefined;

export const isSameSigner = (a: FormSignerMapping, b: FormSignerMapping) => {
  const identity = getIdentity(a);
  return !!identity && identity === getIdentity(b);
};

const isUserBound = (row: FormSignerMapping) => !!row.signerUserId;

export const getLinkedGroups = (rows: FormSignerMapping[]) => {
  const groups = new Map<string, FormSignerMapping[]>();
  for (const row of rows) {
    const identity = getIdentity(row);
    if (!identity) continue;
    groups.set(identity, [...(groups.get(identity) ?? []), row]);
  }
  return [...groups.values()].filter(
    (group) => group.length > 1 && group.some(isUserBound),
  );
};

const lowestOrder = (rows: FormSignerMapping[]) => {
  const orders = rows
    .map((row) => row.signingOrder)
    .filter((order): order is number => Number.isFinite(order));
  return orders.length > 0 ? Math.min(...orders) : undefined;
};

const isFiniteOrder = (order: number | undefined): order is number =>
  Number.isFinite(order);

type SyncLinkedOrdersOptions = {
  savedSignerMapping?: FormSignerMapping[];
  editSigningOrder?: boolean;
};

// Decides the shared order of each linked group when the rows load and when a
// row joins a group. Pass changedKeys to sync only the groups with those rows.
export const syncLinkedOrders = (
  rows: FormSignerMapping[],
  {
    savedSignerMapping = [],
    editSigningOrder = false,
    changedKeys,
  }: SyncLinkedOrdersOptions & { changedKeys?: Set<string> } = {},
) => {
  const sharedOrderByKey = new Map<string, number | undefined>();
  for (const group of getLinkedGroups(rows)) {
    if (changedKeys && !group.some((row) => changedKeys.has(row.signerKey))) {
      continue;
    }
    const savedOrder = editSigningOrder
      ? group
          .map(
            (row) =>
              savedSignerMapping.find(
                (saved) => saved.signerKey === row.signerKey,
              )?.signingOrder,
          )
          .find(isFiniteOrder)
      : undefined;
    const staticRows = group.filter((row) => !isUserBound(row));
    const sharedOrder =
      savedOrder ?? lowestOrder(staticRows) ?? lowestOrder(group);
    for (const row of group) sharedOrderByKey.set(row.signerKey, sharedOrder);
  }

  return rows.map((row) =>
    sharedOrderByKey.has(row.signerKey)
      ? {
          ...row,
          signingOrder: sharedOrderByKey.get(row.signerKey) ?? row.signingOrder,
        }
      : row,
  );
};

// Finds the linked rows. It does not change any order.
export const applyLinks = (rows: FormSignerMapping[]) => ({
  rows,
  linkedKeys: new Set(
    getLinkedGroups(rows).flatMap((group) => group.map((row) => row.signerKey)),
  ),
});

export const updateSignerRow = (
  rows: FormSignerMapping[],
  signerKey: string,
  changes: Partial<FormSignerMapping>,
  syncOptions: SyncLinkedOrdersOptions = {},
) => {
  const edited = rows.find((row) => row.signerKey === signerKey);
  const linkedKeys = new Set(
    'signingOrder' in changes && edited
      ? (getLinkedGroups(rows)
          .find((group) => group.includes(edited))
          ?.map((row) => row.signerKey) ?? [])
      : [],
  );

  const updated = rows.map((row) => {
    if (row.signerKey === signerKey) return { ...row, ...changes };
    if (linkedKeys.has(row.signerKey)) {
      return { ...row, signingOrder: changes.signingOrder };
    }
    return row;
  });

  const updatedRow = updated.find((row) => row.signerKey === signerKey);
  const identityChanged =
    !!edited && !!updatedRow && getIdentity(edited) !== getIdentity(updatedRow);
  if (!identityChanged || 'signingOrder' in changes) return updated;

  return syncLinkedOrders(updated, {
    ...syncOptions,
    changedKeys: new Set([signerKey]),
  });
};
